/* Proposal API client — every /api/architect/proposal endpoint the SPA
   calls, plus the small role helper that decides who may do what.

   The proposal module mirrors the estimate module (async RUN pattern,
   white-label errors). It is mounted at /api/architect/proposal on the
   server, BEFORE the generic /api/architect router. The global fetch
   interceptor attaches the Bearer token, so these plain fetches need no
   auth plumbing — same as api.ts.

   Error copy is white-label throughout: no provider names ever reach the
   operator (the server already classifies provider flows; the map below
   covers the codes this surface can produce). */

import { ERROR_EN } from './utils';
import type { AuthedUser } from '../auth/api';
import { stageAccess } from './stageAccess';

const API = (process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(
  /\/+$/,
  '',
);
const BASE = API + '/api/architect/proposal';

/* Shared stage capabilities keep viewing, editing and approval separate. */
export interface ProposalAccess {
  /** Root can edit prices and the actual client DOCX. Selected Proposal
      reviewers can also see the full client file via server capability. */
  isRoot: boolean;
  canApprove?: boolean;
  approvalConfigured?: boolean;
  canEditClientDocument?: boolean;
  /** Proposal editing and generation. */
  canGenerate: boolean;
  canSee: boolean;
  /** @deprecated Compatibility alias for canSee. */
  canEstimate: boolean;
}

export function proposalAccess(
  user: Pick<AuthedUser, 'isRoot' | 'stage_access'> | null | undefined,
  permissions: string[],
): ProposalAccess {
  const rights = stageAccess(user, permissions, 'proposal');
  return {
    isRoot: !!user?.isRoot,
    canGenerate: rights.edit,
    canSee: rights.view,
    canEstimate: rights.view,
    canApprove: rights.approve,
    approvalConfigured: rights.configured,
    canEditClientDocument: !!rights.edit_client_document,
  };
}

/* ═══════════ TYPES ═════════════════════════════════════════════ */

export type ProposalStatus = 'review' | 'approved';

export type ProposalRunStatus = 'running' | 'done' | 'error';
/** Live sub-step of a proposal run; drives the shared stepper. */
export type ProposalRunStage =
  | 'preparing'
  | 'llm'
  | 'llm-retry'
  | 'validating'
  | 'rendering'
  | 'saving';

export interface ProposalRun {
  id: string;
  project_id: string;
  /** Set at the 'saving' stage once the proposals row exists. */
  proposal_id: string | null;
  status: ProposalRunStatus;
  stage: string; // one of ProposalRunStage, but tolerate unknown values
  model: string | null;
  error: string | null;
  telemetry: unknown | null;
  created_at: string;
  updated_at: string;
  finished_at: string | null;
}

export interface ProposalMeta {
  /** Original author is retained; restoration is an independent Root action. */
  recovery?: { id: string; source_proposal_id: string; restored_by_name: string; restored_at: string };
  id: string;
  project_id: string;
  estimate_id: string | null;
  status: ProposalStatus;
  model: string | null;
  client: string;
  project: string;
  created_at: string;
  created_by_name: string | null;
  approved_by_name: string | null;
  approved_at: string | null;
  updated_at: string | null;
  has_docx: boolean;
  /** True once root has rendered a PRICED document (the «Цены» pipeline stage
      is done). Only a stage flag — never the actual prices, which are stripped
      for non-root callers (DESIGN11 §P2.3). */
  priced: boolean;
  document_revision?: number;
  /** Root's client file has been edited directly. Other roles only receive the lock flag. */
  client_document_edited?: boolean;
  client_document_locked?: boolean;
  can_view_client_document?: boolean;
  can_edit_client_document?: boolean;
  supports_document_patch?: boolean;
}

/* ── spec (LLM output) — the full v1 shape the document editor renders and
      edits inline. Prices, week counts and thresholds are NOT part of it: the
      server injects placeholders and the CEO fills the separate (root-only)
      `pricing` object. Mirrors backend proposal/validate.ts. ── */
export interface ProposalCase {
  name: string;
  opening?: string;
  bullets?: string[];
  closing?: string;
}
/** { name, text, category? } item — api_integrations, confirmations,
    dashboard capabilities all share this shape (category only on APIs). */
export interface ProposalNamedText {
  name: string;
  text: string;
  category?: string;
}
export interface ProposalDashboard {
  intro: string;
  extra?: string;
  capabilities?: ProposalNamedText[];
}
export interface ProposalEfficiency {
  opening?: string;
  development?: string[];
  benefits?: string[];
  closing?: string;
}
export interface ProposalTimelinePhase {
  phase: string;
  details: string;
}
export interface ProposalSetupFee {
  case: string;
  description?: string;
  apis?: string[];
  notes?: string[];
}
export interface ProposalMonthlyFee {
  label: string;
  bullets?: string[];
}
export interface ProposalSpec {
  version?: number;
  client?: string;
  project?: string;
  overview?: string;
  workflow_intro?: string;
  cases?: ProposalCase[];
  please_note?: string;
  api_integrations?: ProposalNamedText[];
  confirmations_intro?: string;
  confirmations?: ProposalNamedText[];
  confirmations_note?: string | null;
  dashboard?: ProposalDashboard | null;
  efficiency?: ProposalEfficiency;
  timeline?: ProposalTimelinePhase[];
  setup_fees?: ProposalSetupFee[];
  monthly_fees?: ProposalMonthlyFee[];
  monthly_notes?: string[];
  [key: string]: unknown;
}

/* ── pricing (server-computed, CEO-editable) ── */
export interface PricingSetup {
  i: number;
  price: string | null;
}
export interface PricingMonthly {
  i: number;
  price: string | null;
  overage: string | null;
}
export interface PricingCaseWeeks {
  case: string;
  weeks: string;
}
export interface ProposalPricing {
  setup: PricingSetup[];
  monthly: PricingMonthly[];
  case_weeks: PricingCaseWeeks[];
  total_weeks: string | null;
}

export interface ProposalFull extends ProposalMeta {
  spec: ProposalSpec;
  /** Root and selected Proposal reviewers receive prices; other permitted
      readers receive null and the unpriced document. */
  pricing: ProposalPricing | null;
}

/** Token/cost telemetry the AI-chat prompt endpoint returns alongside the
    edited proposal. Every field is optional — the footnote renders what it
    has (mirrors the backend ProposalTelemetry, camelCase wire). */
export interface ProposalTelemetry {
  model?: string;
  inputTokens?: number;
  cacheReadTokens?: number;
  cacheCreationTokens?: number;
  outputTokens?: number;
  costUsd?: number;
  durationMs?: number;
}

/* ═══════════ ERROR COPY (white-label) ══════════════════════════ */

const PROPOSAL_ERROR_EN: Record<string, string> = {
  approval_delegate_root_only: 'Only Root can approve for another reviewer.',
  invalid_approval_delegate: 'Choose an active reviewer for this stage.',
  approval_delegate_override_conflict: 'Choose one approval identity.',
  invalid_question: 'Enter a question about the proposal.',
  document_text_unavailable: 'This document could not be read. Reload it and try again.',
  client_document_access_changed: 'Your access changed. Reload the proposal.',
  ask_failed: 'The assistant could not answer. Try again.',
  proposal_already_running:
    'A proposal is already being generated — wait a moment',
  no_approved_estimate: 'The estimate must be approved first',
  no_approved_architecture: 'Approve the architecture first',
  invalid_model: 'That model is not available',
  invalid_pricing: 'Some prices could not be saved — check the fields',
  invalid_spec: 'Some fields need attention before saving',
  invalid_instruction: 'Type what you want changed first',
  render_failed: 'The document could not be rendered — try again',
  proposal_generation_failed:
    'The proposal could not be completed — try again',
  proposal_failed: 'The proposal could not be completed — try again',
  proposal_busy: 'The proposal is being edited — try again in a moment',
  already_decided: 'This proposal has already been approved',
  forbidden: 'Your account cannot perform that action',
  no_docx: 'No document is available for this proposal',
  not_found: 'Proposal not found',
  document_changed: 'The saved document changed. Reload it before continuing.',
  document_revision_required: 'Reload the saved document before approving.',
  client_document_locked: 'The client document has been edited directly. Open the saved document to review it.',
  invalid_document: 'This document could not be saved. Your edits are still here.',
  document_too_large: 'The document is too large to save.',
};

/* ═══════════ FETCH HELPER (mirrors api.ts req/mapError) ═════════ */

export interface ApiError extends Error {
  status?: number;
  code?: string;
}

interface ReqOpts {
  method?: string;
  json?: unknown;
  raw?: boolean;
  headers?: Record<string, string>;
  body?: BodyInit;
}

async function mapError(res: Response): Promise<ApiError> {
  let msg = 'Error ' + res.status;
  let code: string | undefined;
  try {
    const j: any = await res.json();
    code = j.error;
    msg =
      PROPOSAL_ERROR_EN[j.error] ||
      ERROR_EN[j.error] ||
      j.message ||
      j.error ||
      msg;
    if (Array.isArray(j.errors) && j.errors.length) {
      msg +=
        ': ' +
        j.errors.slice(0, 3).join('; ') +
        (j.errors.length > 3 ? ` (+${j.errors.length - 3})` : '');
    }
  } catch (_) {
    /* not json */
  }
  const err = new Error(msg) as ApiError;
  err.status = res.status;
  err.code = code;
  return err;
}

async function req(path: string, opts: ReqOpts = {}): Promise<any> {
  const headers: Record<string, string> = { ...opts.headers };
  const init: RequestInit = { method: opts.method || 'GET', headers };
  if (opts.body !== undefined) init.body = opts.body;
  if (opts.json !== undefined) {
    headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(opts.json);
  }
  let res: Response;
  try {
    res = await fetch(BASE + path, init);
  } catch (e) {
    throw new Error('Network unavailable — check your connection');
  }
  if (!res.ok) throw await mapError(res);
  if (opts.raw) return res;
  const ct = res.headers.get('content-type') || '';
  return ct.includes('application/json') ? res.json() : res.text();
}

/* ═══════════ CLIENT ════════════════════════════════════════════ */

export const proposalApi = {
  // List a project's proposals (visibility already filtered server-side).
  listProposals: (projectId: string): Promise<{ proposals: ProposalMeta[] }> =>
    req('/projects/' + projectId),

  // Start a proposal run — 202 {run}; the caller polls getProposalRun.
  generateProposal: (
    projectId: string,
    model?: string,
  ): Promise<{ run: ProposalRun }> =>
    req('/projects/' + projectId + '/generate', {
      method: 'POST',
      json: { model },
    }),

  // Poll a run by id.
  getProposalRun: (runId: string): Promise<{ run: ProposalRun }> =>
    req('/runs/' + runId),

  // Stop a running proposal run (DESIGN13 §F1) — idempotent server-side
  // (mirrors architectApi.cancelRun). Returns the updated run.
  cancelProposalRun: (runId: string): Promise<{ run: ProposalRun }> =>
    req('/runs/' + runId + '/cancel', { method: 'POST', json: {} }),

  // The project's active run (or the most recent within the resume
  // window) — restores the stepper after a page reload.
  getActiveProposalRun: (
    projectId: string,
  ): Promise<{ run: ProposalRun | null }> =>
    req('/projects/' + projectId + '/runs/active'),

  // Full proposal incl. the editable spec (+ pricing, root-only) — feeds
  // both the pricing editor (root) and the document editor (generator).
  getProposal: (id: string): Promise<{ proposal: ProposalFull }> =>
    req('/' + id),

  // Re-validate + re-render + store an edited proposal spec. The server
  // returns the fresh full proposal with status back to 'review' (a changed
  // document needs re-approval) and pricing stripped for non-root. Prices are
  // never part of the spec, so this endpoint never carries them.
  updateSpec: (
    id: string,
    spec: ProposalSpec,
  ): Promise<{ proposal: ProposalFull }> =>
    req('/' + id + '/spec', { method: 'PUT', json: { spec } }),

  // AI chat edit: apply a natural-language instruction to the proposal spec.
  // The server re-validates and re-renders exactly like a manual save (status
  // → review) and returns the fresh full proposal plus token/cost telemetry.
  // `proposal_busy` (409) is the per-proposal lock backstop — the chat client
  // also serialises its own sends.
  promptEdit: (
    id: string,
    body: { instruction: string; model: string },
  ): Promise<{ proposal: ProposalFull; telemetry?: ProposalTelemetry }> =>
    req('/' + id + '/prompt', { method: 'POST', json: body }),

  // Raw Response so the caller can read the Content-Disposition filename.
  proposalDocxBlob: (id: string): Promise<Response> =>
    req('/' + id + '/docx', { raw: true }),

  updateDocument: async (id: string, bytes: ArrayBuffer, etag: string, patch?: {baseBytes:Uint8Array;supportsPatch:boolean}): Promise<{
    proposal: ProposalFull; document: { etag: string; edited: boolean };
  }> => {
    const body = new FormData();
    const parts=patch?.supportsPatch?(await import('./proposalDocumentParts')).documentPartsPatch(patch.baseBytes,new Uint8Array(bytes)):null;
    if(parts){body.append('mode','parts');body.append('removed_parts',JSON.stringify(parts.removedParts));}
    body.append('document', new Blob([parts?parts.bytes:bytes], {
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    }), parts?'changed-parts.zip':'proposal.docx');
    return req('/' + id + '/document', { method: 'PUT', body, headers: { 'If-Match': etag } });
  },

  // The architect (generator/root) approves — estimator gets 403.
  approveProposal: (id: string, document?: { revision?: number; etag?: string; approve_as_user_id?: string }): Promise<{ proposal: ProposalMeta; approvals?: { given: number; required: number } }> =>
    req('/' + id + '/approve', { method: 'POST',
      json: document ? { ...(document.revision !== undefined ? { document_revision: document.revision } : {}), ...(document.approve_as_user_id ? { approve_as_user_id: document.approve_as_user_id } : {}) } : {},
      headers: document?.etag ? { 'If-Match': document.etag } : undefined,
    }),

  askProposal: (id: string, input: { question: string; model?: string }): Promise<{ answer: string; telemetry?: ProposalTelemetry | null }> =>
    req('/' + id + '/ask', { method: 'POST', json: input }),

  // Root sets/edits prices → re-render → returns the fresh full proposal.
  updatePricing: (
    id: string,
    pricing: ProposalPricing,
  ): Promise<{ proposal: ProposalFull }> =>
    req('/' + id + '/pricing', { method: 'PUT', json: { pricing } }),
};
